/**
 * The prospect surface's half of the resumable upload client.
 *
 * The protocol, the resume, the retry and the pipeline poll all live in `@iqz-ddds/components`, because
 * DG.1 gave a representative an upload too and almost all of that code is failure handling - the part
 * that never runs during development and runs on the worst day. Two copies of it would drift, and the
 * drift would be in the resume, which is what section 09's whole tus decision rests on.
 *
 * What is left here is the part that is genuinely this surface's: where its endpoints are, that its
 * credential is a cookie rather than a header, and what a failure says to somebody who clicked a link
 * out of their inbox and is not yet sure any of this is real. `guest-failures.ts` was careful about
 * that copy and this is where it stays.
 */
import { uploadFile as upload, xhrSend, type UploadHandle, type UploadRequest, type UploadTransport } from '@iqz-ddds/components';
import { problemDetailsSchema } from '@iqz-ddds/contracts';
import { ApiError, OfflineError } from '../api/client';

export { formatBytes } from '@iqz-ddds/components';
export type { UploadHandle, UploadProgress, UploadRequest } from '@iqz-ddds/components';

const UPLOADS = '/guest/v1/uploads';

/**
 * How this surface reaches its uploads.
 *
 * `credentials: 'include'` on every call, for the reason `api/client.ts` gives: the session is a
 * host-scoped `SameSite=Lax` cookie and the browser will not send it otherwise. Same origin in
 * production; the Vite proxy is what makes it same origin in development.
 *
 * Creation is under the exchange and the per-upload calls are not, which mirrors the server: an
 * upload row already knows its exchange, so repeating it in those paths would be a second place for
 * the two to disagree. D-25 is why creation needs it at all - a session names a contact now, and a
 * contact can be sending to more than one representative.
 */
function transportFor(exchangeId: string): UploadTransport {
  return {
    createUrl: `/guest/v1/exchanges/${encodeURIComponent(exchangeId)}/uploads`,
    uploadsUrl: UPLOADS,

    send: (url, init) => fetch(url, { ...init, credentials: 'include' }),

    // DG.21. The chunk `PATCH` only, and the same cookie: this surface is the one on a phone
    // on cellular, where an 8MB chunk is several seconds of a bar that used to sit still.
    sendWithProgress: (url, init, onSent) => xhrSend(url, init, onSent, { withCredentials: true }),

    async failure(response, fallback) {
      // The server's sentence where there is one. `guest-failures.ts` is explicit that nothing on this
      // surface says "invalid", "denied" or "forbidden", so replacing its copy would throw that away.
      let detail = fallback;
      try {
        const parsed = problemDetailsSchema.safeParse(await response.json());
        if (parsed.success && parsed.data.detail !== undefined && parsed.data.detail !== '') {
          detail = parsed.data.detail;
        }
      } catch {
        // No body, or not JSON. The fallback is already the right sentence.
      }
      return new ApiError(response.status, detail, response.headers.get('x-correlation-id') ?? undefined);
    },

    offline: () => new OfflineError(),
  };
}

/** Upload a file, resuming across dropped connections, and wait for the pipeline's verdict. */
export function uploadFile(exchangeId: string, input: UploadRequest & { readonly maxBytes?: number }): Promise<UploadHandle> {
  return upload(transportFor(exchangeId), {
    ...input,
    // Refused before a byte is sent, which is section 09's instruction. Asking a person on cellular to
    // send 400MB before telling them it is too large is the rudest possible way to enforce a limit.
    tooLarge: (size, max) => new ApiError(413, `That file is ${size}, and the largest this accepts is ${max}.`, undefined),
  });
}

function genericTransportFor(token: string): UploadTransport {
  return {
    createUrl: `/guest/v1/upload-links/${encodeURIComponent(token)}/uploads`,
    uploadsUrl: UPLOADS,

    // The guest cookie set by `/sign-in/verify`, not anything this file adds: the server derives
    // the sender's name and address from that session rather than trusting a client-declared one.
    send: (url, init) => fetch(url, { ...init, credentials: 'include' }),

    sendWithProgress: (url, init, onSent) => xhrSend(url, init, onSent, { withCredentials: true }),

    async failure(response, fallback) {
      let detail = fallback;
      try {
        const parsed = problemDetailsSchema.safeParse(await response.json());
        if (parsed.success && parsed.data.detail !== undefined && parsed.data.detail !== '') {
          detail = parsed.data.detail;
        }
      } catch {
        // No body, or not JSON.
      }
      return new ApiError(response.status, detail, response.headers.get('x-correlation-id') ?? undefined);
    },

    offline: () => new OfflineError(),
  };
}

/** Upload a file via a generic upload link, once its OTP-verified session cookie is set. */
export function uploadGenericFile(token: string, input: UploadRequest & { readonly maxBytes?: number }): Promise<UploadHandle> {
  return upload(genericTransportFor(token), {
    ...input,
    tooLarge: (size, max) => new ApiError(413, `That file is ${size}, and the largest this accepts is ${max}.`, undefined),
  });
}
